import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";
import Chart from "react-google-charts";
import classNames from "classnames";
import mixpanel from "mixpanel-browser";

import { runEnergyPlus, fetchEnergyPlusResults } from "features/Model/_ducks/thunk";
import { selectEnergyPlusResults, selectModelName } from "features/Model/_ducks/selectors";
import { useStateSelector } from "utils/field";
import { buildHpxmlAuditReport } from "utils/energyPlus/buildHpxmlAuditReport";
import {
    parseAnnualResults,
    getFuelUseChartData,
    getEndUseChartData,
    getEmissionsChartData,
    mbtuToGJ,
    lbToMetricTons,
    getAvailableHourlyMetrics,
    formatHourlyChartData,
    getMetricUnit,
} from "utils/results/energyPlus";

import Button from "components/Button";
import Select from "components/Input/Select";
import Accordion from "components/Accordion";
import LoadingDots from "components/LoadingDots";
import { ChartTable } from "features/Model/Review/Results/General";
import Dialog, { CloseDialog } from "components/Dialog";

import classes from "./styles.module.scss";
import resultsClasses from "features/Model/Review/Results/style.module.scss";

import dummyResults from "features/Model/Review/EnergyPlus/tempResults.json";
import { downloadTextFile } from "utils/downloadTextFile";
import { formatAuditFetchError } from "utils/energyPlus/auditUserMessages";
import HpxmlAuditDrawer from "features/Model/Review/EnergyPlus/HpxmlAuditDrawer";

import HelpIcon from "assets/images/icons/JSX/Help";

const WORKSPACE_HOUSE_PATH = "/workspace/house/";
const stripWorkspaceHouseFromLogLine = (line) =>
    typeof line === "string" ? line.split(WORKSPACE_HOUSE_PATH).join("") : line;
const sanitizeLogLines = (lines = []) => lines.map(stripWorkspaceHouseFromLogLine);

const SimulationLogsBlock = ({ errors = [], warnings = [] }) => (
    <div
        style={{
            marginTop: "1rem",
            padding: "1rem",
            borderRadius: "0.5rem",
            backgroundColor: "#fafbfc",
            border: "1px solid #e1eaf0",
        }}
    >
        <h4 style={{ margin: "0 0 0.75rem", fontSize: "0.9375rem", fontWeight: 700, color: "#1a1a1a" }}>
            Simulation logs
        </h4>
        <p style={{ margin: "0 0 0.35rem", fontSize: "0.8125rem", fontWeight: 700, color: "#646f81" }}>Errors</p>
        {errors.length > 0 ? (
            <ul style={{ margin: "0 0 1rem 1.25rem", padding: 0 }}>
                {errors.map((error, i) => (
                    <li style={{ marginBottom: "0.5rem", fontSize: "0.875rem", color: "#c21818" }} key={`err-${i}`}>
                        {error}
                    </li>
                ))}
            </ul>
        ) : (
            <p style={{ margin: "0 0 1rem", fontSize: "0.875rem", color: "#646f81" }}>None</p>
        )}
        <p style={{ margin: "0 0 0.35rem", fontSize: "0.8125rem", fontWeight: 700, color: "#646f81" }}>Warnings</p>
        {warnings.length > 0 ? (
            <ul style={{ margin: "0 0 0 1.25rem", padding: 0 }}>
                {warnings.map((warning, i) => (
                    <li style={{ marginBottom: "0.5rem", fontSize: "0.875rem", color: "#f58020" }} key={`warn-${i}`}>
                        {warning}
                    </li>
                ))}
            </ul>
        ) : (
            <p style={{ margin: 0, fontSize: "0.875rem", color: "#646f81" }}>None</p>
        )}
    </div>
);

const ALLOWED_H2K_VERSIONS = ["v11-11", "v11-12", "v11-13"];

const EnergyPlus = ({
    modelSave,
    validateH2kExport,
    modelId,
    runEnergyPlus,
    fetchEnergyPlusResults,
    energyPlusResults = {},
    modelName = "Model",
}) => {
    const {
        data: { usage_data = {}, loads_temps_data = {}, annual_results = {} } = {},
        logs: { errors = [], warnings = [] } = {},
        hpxml = null,
        metaData: {
            simulation: { upgradePackage: { label: upgradePackageLabel = "Base", upgradePackageId = "" } = {} } = {},
        } = {},
    } = energyPlusResults;

    const displayErrors = sanitizeLogLines(errors);
    const displayWarnings = sanitizeLogLines(warnings);

    const [isRunning, setIsRunning] = useState(false);
    const [isPreparing, setIsPreparing] = useState(false);
    const [isLoadingFromDb, setIsLoadingFromDb] = useState(false);
    const [latestRunFailed, setLatestRunFailed] = useState(false);
    const [upgradeSelected, toggleUpgradeSelected] = useState(upgradePackageId);
    const [leftMetric, setLeftMetric] = useState(null);
    const [rightMetric, setRightMetric] = useState(null);
    const [auditOpen, setAuditOpen] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditError, setAuditError] = useState(null);
    const [auditReport, setAuditReport] = useState(null);
    const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);

    const { packages = {} } = useStateSelector("upgrades") || {};
    const modelData = useStateSelector("model.modelData") || {};
    let { major: h2kMajor, minor: h2kMinor } = modelData.h2kVersion || {};
    const h2kVersion = `v${h2kMajor}-${h2kMinor}`;

    const regionId = modelData?.location?.weather?.region?.id || 5;

    const upgradeExportOpts = Object.keys(packages)
        .filter((pkg) => pkg !== "base")
        .map((pkg) => ({
            value: pkg,
            label: packages[pkg].name,
        }));

    const lastRunAttemptUpgradeLabel = upgradeSelected
        ? upgradeExportOpts.find(({ value }) => value === upgradeSelected)?.label || "Base"
        : "Base";

    useEffect(() => {
        // Reset the Applied Upgrade Package if the selected upgrade package is not found in the packages list
        if (!Object.keys(packages).includes(upgradePackageId)) {
            toggleUpgradeSelected("");
        }
    }, []);

    // const selectedUpgrade = upgradeSelected ? upgradeExportOpts.find(({ value }) => value === upgradeSelected) : {};

    const runEnergyPlusSimulation = async () => {
        if (!ALLOWED_H2K_VERSIONS.includes(h2kVersion)) {
            return;
        }

        try {
            setIsRunning(true);
            setLatestRunFailed(false);

            const selectedUpgrade = upgradeSelected
                ? upgradeExportOpts.find(({ value }) => value === upgradeSelected)
                : {};
            const upgradePackageLabel = selectedUpgrade?.label || "Base";

            mixpanel.track("EnergyPlus Run Start", {
                ModelId: modelId,
                UpgradePackageId: upgradeSelected || "base",
                UpgradePackageLabel: upgradePackageLabel,
                H2kVersion: h2kVersion,
            });

            const result = await runEnergyPlus(modelId, h2kVersion, {
                upgradePackageId: upgradeSelected,
                label: upgradePackageLabel,
            });
            if (!result?.ok) {
                setLatestRunFailed(true);
            }
        } catch (error) {
            console.log(error.message);
        } finally {
            setIsRunning(false);
        }
    };

    const prepareEnergyPlus = async () => {
        setIsPreparing(true);

        await modelSave();

        const errorList = await validateH2kExport({ modelId, h2kVersion });

        // if (errorList?.length > 0) {
        //     setIsPreparing(false);

        // console.log(errorList);
        //     return;
        // }

        runEnergyPlusSimulation();
        setIsPreparing(false);
    };

    // Load any previously saved EnergyPlus results when landing on the tab.
    useEffect(() => {
        if (!modelId) return;

        const load = async () => {
            setIsLoadingFromDb(true);
            try {
                await fetchEnergyPlusResults(modelId);
            } finally {
                setIsLoadingFromDb(false);
            }
        };

        load();
    }, [modelId, fetchEnergyPlusResults]);

    const closeAuditDrawer = () => {
        setAuditOpen(false);
        setAuditError(null);
    };

    const openAuditDrawer = async () => {
        if (!hpxml) return;
        setAuditOpen(true);
        setAuditLoading(true);
        setAuditError(null);
        setAuditReport(null);
        try {
            let text = hpxml;
            if (typeof hpxml === "string" && /^https?:\/\//i.test(hpxml)) {
                const res = await fetch(hpxml);
                text = await res.text();
            }
            setAuditReport(buildHpxmlAuditReport(text, modelData, packages, upgradePackageId));
        } catch (err) {
            console.error("[HPXML audit] fetch or unexpected error while opening audit", err?.message);
            // if (err instanceof Error && err.stack) {
            //     console.error(err.stack);
            // }
            setAuditError(formatAuditFetchError(err instanceof Error ? err : new Error(String(err))));
        } finally {
            setAuditLoading(false);
        }
    };

    // Parse annual results for display
    const parsedResults = parseAnnualResults(annual_results, regionId);
    const fuelUseChartData = getFuelUseChartData(parsedResults.fuelUse);
    const endUseChartData = getEndUseChartData(parsedResults.endUseElectricity, parsedResults.endUseNaturalGas);
    const emissionsChartData = getEmissionsChartData(parsedResults.emissions);

    // Get available hourly metrics
    const availableMetrics = getAvailableHourlyMetrics(usage_data, loads_temps_data);
    const hourlyChartData = formatHourlyChartData(
        usage_data,
        loads_temps_data,
        leftMetric,
        rightMetric,
        energyPlusResults?.data?.startTime
    );

    // Get units for selected metrics
    const leftMetricLabel = leftMetric ? availableMetrics.find((m) => m.value === leftMetric)?.label || "" : "";
    const rightMetricLabel = rightMetric ? availableMetrics.find((m) => m.value === rightMetric)?.label || "" : "";
    const leftUnit = getMetricUnit(leftMetricLabel);
    const rightUnit = getMetricUnit(rightMetricLabel);

    // Helper functions for charts
    const pieChartTooltip = (title, value, unit, percentage) => {
        return `<div style="min-width:140px; padding: 10px 12px;">
            <p style="font-weight:700; margin-bottom: 0; line-height: 16px;">${title}</p>
            <p>${value} ${unit} <span style="margin-left:4px; color:#646f81;">(${percentage})</span></p>
        </div>`;
    };

    const getPercentage = (value, total) => `${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%`;

    const Stat = ({ label = "", value = 0, unit = "", decimals = 2, big = false }) => (
        <div className={classNames(resultsClasses.stat, { [resultsClasses.bigStat]: big })}>
            <p>{label}</p>
            <span>
                {unit === "$" && unit}
                <strong>
                    {typeof value === "number" ? value.toFixed(decimals).replace("-0.00", "0.00") : value}
                </strong>{" "}
                {unit !== "$" && unit && unit}
            </span>
        </div>
    );

    const PieChart = ({ data, title, isEmpty = false }) => {
        if (!data || data.length <= 1) {
            return (
                <div className={resultsClasses.pieChart}>
                    <h3>{title}</h3>
                    <div className={resultsClasses.pieChartEmpty}>No data available</div>
                </div>
            );
        }

        // Calculate total and get unit from data header
        const total = data.slice(1).reduce((sum, row) => sum + row[1], 0);
        const unit = data[0][1]; // Get unit from header row (GJ or tCO₂)

        // Format total for display
        const formattedTotal = total.toFixed(2);
        const displayTitle = `${title} (Total: ${formattedTotal} ${unit})`;

        // Add tooltips to data
        const dataWithTooltips = [
            ["Type", "Value", { type: "string", role: "tooltip", p: { html: true } }],
            ...data
                .slice(1)
                .map((row) => [
                    row[0],
                    parseFloat(row[1].toFixed(2)),
                    pieChartTooltip(row[0], parseFloat(row[1].toFixed(2)), unit, getPercentage(row[1], total)),
                ]),
        ];

        return (
            <div className={resultsClasses.pieChart}>
                <h3>{displayTitle}</h3>
                {isEmpty ? (
                    <div className={resultsClasses.pieChartEmpty}>Net Zero</div>
                ) : (
                    <Chart
                        width={"100%"}
                        chartType="PieChart"
                        loader={LoadingDots}
                        data={dataWithTooltips}
                        options={{
                            chartArea: {
                                top: 40,
                                left: 0,
                                width: "95%",
                                height: "75%",
                            },
                            sliceVisibilityThreshold: 0.01,
                            pieSliceBorderColor: "none",
                            tooltip: {
                                showColorCode: true,
                                textStyle: { fontName: "Noto Sans TC", fontSize: 12 },
                                isHtml: true,
                            },
                            legend: {
                                position: "right",
                                alignment: "center",
                                textStyle: { fontName: "Noto Sans TC", color: "#7B7B7B", fontSize: 12 },
                            },
                            colors: [
                                "#0049c6", //Heating
                                "#f58020", //Hot Water
                                "#18c1ad", //Plug Loads
                                "#c21818", //Appliances
                                "#FFB22D", //Lighting
                                "#1b97ba", //Cooling
                            ],
                        }}
                    />
                )}
            </div>
        );
    };

    return (
        <div className={classes.energyPlusContainer}>
            <div className={classes.energyPlusButtonsContainer}>
                <div>
                    <div className={classes.headerWithIcon}>
                        <h4>EnergyPlus Simulations</h4>
                        <button
                            className={classes.helpButton}
                            onClick={() => setIsInfoDialogOpen(true)}
                            aria-label="EnergyPlus Simulation Information"
                        >
                            <HelpIcon />
                        </button>
                    </div>
                    <p style={{ marginTop: "1rem", fontSize: "0.875rem", color: "$grey-mid" }}>
                        Leveraging the{" "}
                        <a
                            href="https://github.com/canmet-energy/h2k-hpxml"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#0049c6", textDecoration: "underline" }}
                        >
                            H2k-HPXML
                        </a>{" "}
                        translator and{" "}
                        <a
                            href="https://openstudio-hpxml.readthedocs.io/en/latest/index.html#"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#0049c6", textDecoration: "underline" }}
                        >
                            OS-HPXML
                        </a>{" "}
                        workflow.
                    </p>
                </div>

                <Select
                    name="upgradePackageApplied"
                    options={[
                        {
                            value: "",
                            label: "None",
                        },
                        ...upgradeExportOpts,
                    ]}
                    label="Applied Upgrade Package"
                    placeholder="None"
                    input={{
                        value: upgradeSelected,
                        onChange: (value) => toggleUpgradeSelected(value),
                    }}
                    large
                    info={
                        "The upgrade package that will be applied to the EnergyPlus simulation, overwriting the base content of the file for the upgraded sections."
                    }
                />
                <Button
                    onClick={prepareEnergyPlus}
                    disabled={isPreparing || isRunning || !ALLOWED_H2K_VERSIONS.includes(h2kVersion)}
                >
                    {isPreparing ? "Preparing..." : isRunning ? "Running..." : "Run EnergyPlus"}
                </Button>

                {hpxml ? (
                    <div style={{ marginTop: "0.75rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                        <Button
                            onClick={async () => {
                                const filename = `${modelName}.xml`;

                                if (typeof hpxml === "string" && /^https?:\/\//i.test(hpxml)) {
                                    try {
                                        const res = await fetch(hpxml);
                                        const text = await res.text();
                                        downloadTextFile(filename, text);
                                    } catch (err) {
                                        console.warn("HPXML download failed:", err?.message);
                                    }
                                    return;
                                }

                                downloadTextFile(filename, hpxml);
                            }}
                            disabled={isPreparing || isRunning || isLoadingFromDb}
                        >
                            Download HPXML
                        </Button>
                        <Button onClick={openAuditDrawer} disabled={isPreparing || isRunning || isLoadingFromDb}>
                            Audit HPXML
                        </Button>
                    </div>
                ) : null}
            </div>
            {(isRunning || isPreparing) && (
                <div
                    style={{
                        marginTop: "1.5rem",
                        padding: "0.5rem 1rem",
                        backgroundColor: "#f6f8f9",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                    }}
                >
                    <p style={{ margin: 0, display: "flex", alignItems: "center", color: "#646f81" }}>
                        <span style={{ marginRight: "0.75rem" }}>
                            <LoadingDots />
                        </span>
                        {isPreparing
                            ? "Preparing EnergyPlus simulation..."
                            : "EnergyPlus simulation is running. The results should appear momentarily."}
                    </p>
                </div>
            )}
            {!isRunning && !isPreparing && isLoadingFromDb && isEmpty(energyPlusResults) && (
                <div className={classes.noResultsContainer}>
                    <p>Loading EnergyPlus results...</p>
                </div>
            )}

            {!isRunning && !isPreparing && !isLoadingFromDb && isEmpty(energyPlusResults) && (
                <div className={classes.noResultsContainer}>
                    <p>
                        No results to display. Once ready, click <strong>Run EnergyPlus</strong> to see your model's
                        performance.
                    </p>
                    <p>
                        To run an EnergyPlus simulation, you must have first run a HOT2000 simulation that returned
                        results.
                    </p>
                    <p>EnergyPlus simulations can only be run on one model or upgrade package at a time.</p>
                </div>
            )}

            {!isRunning && !isPreparing && !isEmpty(energyPlusResults) && latestRunFailed && (
                <div className={classes.resultsContainer}>
                    <p style={{ marginTop: "1rem", marginBottom: "1rem", fontWeight: "bold" }}>Results</p>
                    <div
                        style={{
                            marginBottom: "1rem",
                            padding: "0.75rem 1rem",
                            borderRadius: "0.5rem",
                            backgroundColor: "#fff5e6",
                            color: "#7a4a00",
                            border: "1px solid #ffd59a",
                            fontSize: "0.875rem",
                        }}
                    >
                        <p style={{ margin: 0 }}>
                            The latest EnergyPlus run did not return results. Upgrade package for this run:{" "}
                            <strong>{lastRunAttemptUpgradeLabel}</strong>. See simulation logs below for details from
                            the API.
                        </p>
                        <p style={{ marginTop: "0.5rem" }}>
                            Any successful results from previous runs will be displayed the next time you navigate to
                            this page.
                        </p>
                    </div>
                    <SimulationLogsBlock errors={displayErrors} warnings={displayWarnings} />
                </div>
            )}

            {!isRunning && !isPreparing && !isEmpty(energyPlusResults) && !latestRunFailed && (
                <div className={classes.resultsContainer}>
                    <p style={{ marginTop: "1rem", marginBottom: "1rem", fontWeight: "bold" }}>Results</p>
                    <div
                        style={{
                            marginBottom: "1rem",
                            padding: "0.75rem 1rem",
                            borderRadius: "0.5rem",
                            backgroundColor: "#f6f8f9",
                            color: "#646f81",
                            border: "1px solid #e1eaf0",
                            fontSize: "0.875rem",
                        }}
                    >
                        <p>
                            Last simulation run{" "}
                            <strong>{energyPlusResults?.metaData?.simulation?.simulationTime || "N/A"}</strong>. Upgrade
                            package applied:{" "}
                            <strong>{energyPlusResults?.metaData?.simulation?.upgradePackage?.label || "Base"}</strong>.
                        </p>
                    </div>
                    {/* NEW RESULTS DISPLAY GOES HERE -------------------------------- */}
                    <div
                        className={`${resultsClasses.row} ${resultsClasses.totals} ${resultsClasses.lastTotals}`}
                        style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}
                    >
                        <Stat
                            label="Total Annual Energy Use"
                            value={mbtuToGJ(parsedResults.totalEnergy)}
                            unit="GJ"
                            decimals={2}
                            big
                        />
                        <Stat
                            label="Annual Heating Load"
                            value={mbtuToGJ(parsedResults.loads.heating)}
                            unit="GJ"
                            decimals={2}
                        />
                        <Stat
                            label="Annual Cooling Load"
                            value={mbtuToGJ(parsedResults.loads.cooling)}
                            unit="GJ"
                            decimals={2}
                        />
                        <Stat
                            label="Annual Emissions"
                            value={lbToMetricTons(parsedResults.emissions.total)}
                            unit="tCO₂"
                            decimals={2}
                        />
                    </div>

                    <div
                        className={`${resultsClasses.row} ${resultsClasses.totals}`}
                        style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}
                    >
                        <Stat
                            label="Peak Electricity (Winter)"
                            value={parsedResults.peakElectricity.winter}
                            unit="W"
                            decimals={1}
                        />
                        <Stat
                            label="Peak Electricity (Summer)"
                            value={parsedResults.peakElectricity.summer}
                            unit="W"
                            decimals={1}
                        />
                        <Stat
                            label="Unmet Heating Hours"
                            value={parsedResults.unmetHours.heating}
                            unit="hr"
                            decimals={0}
                        />
                        <Stat
                            label="Unmet Cooling Hours"
                            value={parsedResults.unmetHours.cooling}
                            unit="hr"
                            decimals={0}
                        />
                    </div>

                    <div
                        className={`${resultsClasses.row} ${resultsClasses.charts}`}
                        style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
                    >
                        <PieChart title="Energy End Use" data={endUseChartData} />
                        <PieChart title="Fuel Use" data={fuelUseChartData} />
                        <PieChart title={`Emissions - ${parsedResults.emissionsSource}`} data={emissionsChartData} />
                    </div>

                    <div
                        className={`${resultsClasses.row} ${resultsClasses.chartBreakdown}`}
                        style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
                    >
                        <ChartTable
                            title="Energy end use"
                            rows={
                                endUseChartData && endUseChartData.length > 1
                                    ? endUseChartData
                                          .slice(1)
                                          .sort((a, b) => b[1] - a[1])
                                          .map((row) => ({
                                              label: row[0],
                                              value: `${parseFloat(row[1]).toFixed(2)} GJ`,
                                          }))
                                    : []
                            }
                        />
                        <ChartTable
                            title="Energy fuel type"
                            rows={
                                fuelUseChartData && fuelUseChartData.length > 1
                                    ? fuelUseChartData
                                          .slice(1)
                                          .sort((a, b) => b[1] - a[1])
                                          .map((row) => ({
                                              label: row[0],
                                              value: `${parseFloat(row[1]).toFixed(2)} GJ`,
                                          }))
                                    : []
                            }
                        />
                        <ChartTable
                            title="Emissions"
                            rows={
                                emissionsChartData && emissionsChartData.length > 1
                                    ? emissionsChartData
                                          .slice(1)
                                          .sort((a, b) => b[1] - a[1])
                                          .map((row) => ({
                                              label: row[0],
                                              value: `${parseFloat(row[1]).toFixed(2)} tCO₂`,
                                          }))
                                    : []
                            }
                        />
                    </div>

                    {/* Hourly Chart Section */}
                    <div
                        style={{
                            marginTop: "3rem",
                            paddingTop: "2rem",
                            borderTop: "1px solid #E5E7EB",
                            paddingBottom: "2rem",
                            borderBottom: "1px solid #E5E7EB",
                            marginBottom: "1rem",
                        }}
                    >
                        <h3 style={{ marginBottom: "1.5rem", fontWeight: "bold" }}>Hourly Results</h3>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "1rem",
                                marginBottom: "1.5rem",
                            }}
                        >
                            <Select
                                name="leftMetric"
                                label="Left Y-Axis"
                                options={[
                                    { value: "", label: "None" },
                                    ...availableMetrics.map((metric) => ({
                                        value: metric.value,
                                        label: metric.label,
                                    })),
                                ]}
                                input={{
                                    value: leftMetric || "",
                                    onChange: (value) => setLeftMetric(value || null),
                                }}
                            />
                            <Select
                                name="rightMetric"
                                label="Right Y-Axis"
                                options={[
                                    { value: "", label: "None" },
                                    ...availableMetrics.map((metric) => ({
                                        value: metric.value,
                                        label: metric.label,
                                    })),
                                ]}
                                input={{
                                    value: rightMetric || "",
                                    onChange: (value) => setRightMetric(value || null),
                                }}
                            />
                        </div>

                        {hourlyChartData && (leftMetric || rightMetric) ? (
                            <div style={{ width: "100%", minHeight: "400px" }}>
                                <Chart
                                    width={"100%"}
                                    height={"400px"}
                                    chartType="LineChart"
                                    loader={LoadingDots}
                                    data={hourlyChartData}
                                    options={{
                                        chartArea: {
                                            left: 80,
                                            right: rightMetric ? 80 : 40,
                                            top: 40,
                                            bottom: 60,
                                            width: "100%",
                                            height: "100%",
                                        },
                                        hAxis: {
                                            title: "Hour of Year",
                                            titleTextStyle: {
                                                fontName: "Noto Sans TC",
                                                fontSize: 12,
                                                color: "#646f81",
                                            },
                                            textStyle: { fontName: "Noto Sans TC", fontSize: 11, color: "#646f81" },
                                            gridlines: { color: "#E5E7EB" },
                                        },
                                        vAxis: {
                                            title: leftMetricLabel
                                                ? `${leftMetricLabel}${leftUnit ? ` (${leftUnit})` : ""}`
                                                : rightMetricLabel
                                                  ? `${rightMetricLabel}${rightUnit ? ` (${rightUnit})` : ""}`
                                                  : "",
                                            titleTextStyle: {
                                                fontName: "Noto Sans TC",
                                                fontSize: 12,
                                                color: "#646f81",
                                            },
                                            textStyle: { fontName: "Noto Sans TC", fontSize: 11, color: "#646f81" },
                                            gridlines: { color: "#E5E7EB" },
                                        },
                                        vAxes: rightMetric
                                            ? {
                                                  1: {
                                                      title: `${rightMetricLabel}${rightUnit ? ` (${rightUnit})` : ""}`,
                                                      titleTextStyle: {
                                                          fontName: "Noto Sans TC",
                                                          fontSize: 12,
                                                          color: "#646f81",
                                                      },
                                                      textStyle: {
                                                          fontName: "Noto Sans TC",
                                                          fontSize: 11,
                                                          color: "#646f81",
                                                      },
                                                      gridlines: { color: "transparent" },
                                                  },
                                              }
                                            : {},
                                        series: (() => {
                                            const series = {};
                                            if (leftMetric) {
                                                series[0] = {
                                                    targetAxisIndex: 0,
                                                    color: "#0049c6",
                                                    lineWidth: 2,
                                                    pointSize: 0,
                                                    visibleInLegend: true,
                                                };
                                            }
                                            if (rightMetric) {
                                                series[leftMetric ? 1 : 0] = {
                                                    targetAxisIndex: 1,
                                                    color: "#f58020",
                                                    lineWidth: 2,
                                                    pointSize: 0,
                                                    visibleInLegend: true,
                                                };
                                            }
                                            return series;
                                        })(),
                                        legend: {
                                            position: "top",
                                            alignment: "center",
                                            textStyle: { fontName: "Noto Sans TC", fontSize: 12, color: "#646f81" },
                                        },
                                        tooltip: {
                                            textStyle: { fontName: "Noto Sans TC", fontSize: 12 },
                                            trigger: "focus",
                                        },
                                        focusTarget: "category",
                                        pointSize: 0,
                                        lineWidth: 2,
                                        colors: ["#0049c6", "#f58020"],
                                    }}
                                />
                            </div>
                        ) : (
                            <div
                                style={{
                                    padding: "3rem",
                                    textAlign: "center",
                                    color: "#646f81",
                                    backgroundColor: "#f6f8f9",
                                    borderRadius: "4px",
                                }}
                            >
                                <p>Select at least one metric to display the hourly chart.</p>
                            </div>
                        )}
                    </div>

                    <Accordion heading="Raw Results Data">
                        {/* Raw results display (keeping existing) */}
                        <div style={{ marginTop: "3rem", paddingTop: "2rem", borderTop: "1px solid #E5E7EB" }}>
                            <p style={{ marginBottom: "1rem", fontWeight: "bold" }}>Raw Results Data</p>
                            {Object.entries(usage_data).map(([key, value]) => (
                                <div key={key}>
                                    <span>{key}:</span>
                                    <span>{JSON.stringify(value).slice(0, 100)}</span>
                                </div>
                            ))}
                            <div style={{ margin: "1rem 0" }} />
                            {Object.entries(loads_temps_data).map(([key, value]) => (
                                <div key={key}>
                                    <span>{key}:</span>
                                    <span>{JSON.stringify(value).slice(0, 100)}</span>
                                </div>
                            ))}
                            <div style={{ margin: "1rem 0" }} />
                            {Object.entries(annual_results).map(([key, value]) => (
                                <div key={key}>
                                    <span>{key}:</span>
                                    <span>{value}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ margin: "1rem 0" }} />
                        <span>--------------------------------</span>
                        <p style={{ marginTop: "1rem", marginBottom: "1rem", fontWeight: "bold" }}>Logs:</p>
                        {displayErrors.length > 0 ? (
                            <ul style={{ marginLeft: "1rem", marginBottom: "1rem" }}>
                                <p style={{ marginBottom: "0.5 rem", fontWeight: "bold" }}>Errors:</p>
                                {displayErrors.map((error, i) => (
                                    <li
                                        style={{ marginBottom: "0.5 rem", fontSize: "0.875rem", color: "#c21818" }}
                                        key={`acc-err-${i}`}
                                    >
                                        {error}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p style={{ marginLeft: "1rem", marginBottom: "1rem" }}>No errors</p>
                        )}
                        {displayWarnings.length > 0 ? (
                            <ul style={{ marginLeft: "1rem" }}>
                                <p style={{ marginBottom: "0.5 rem", fontWeight: "bold" }}>Warnings:</p>
                                {displayWarnings.map((warning, i) => (
                                    <li
                                        key={`acc-warn-${i}`}
                                        style={{ marginBottom: "0.5 rem", fontSize: "0.875rem", color: "#f58020" }}
                                    >
                                        {warning}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p>No warnings</p>
                        )}
                    </Accordion>
                </div>
            )}
            <HpxmlAuditDrawer
                open={auditOpen}
                close={closeAuditDrawer}
                loading={auditLoading}
                error={auditError}
                report={auditReport}
            />
            <Dialog open={isInfoDialogOpen} closeActions={() => setIsInfoDialogOpen(false)}>
                <div className={classes.infoDialog}>
                    <CloseDialog closeActions={() => setIsInfoDialogOpen(false)} />
                    <h2>EnergyPlus Simulations</h2>
                    <div className={classes.dialogContent}>
                        <p>
                            EnergyPlus simulations can now be performed within Volta SNAP using the{" "}
                            <a
                                href="https://github.com/canmet-energy/h2k-hpxml"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#0049c6", textDecoration: "underline" }}
                            >
                                H2k-HPXML
                            </a>{" "}
                            translator and{" "}
                            <a
                                href="https://openstudio-hpxml.readthedocs.io/en/latest/index.html#"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#0049c6", textDecoration: "underline" }}
                            >
                                OS-HPXML
                            </a>{" "}
                            (v1.12.0) workflows. OpenStudio 3.11.0 SDK is used for simulations.
                        </p>
                        <p>During this alpha testing phase, there are some key limitations to be aware of:</p>
                        <ul>
                            <li>Mutli-unit residential buildings are not yet explicitly supported.</li>
                            <li>All EnergyPlus simulations enforce Standard Operating Conditions.</li>
                            <li>
                                Matching EnerGuide assumptions, an equivalent heating setpoint of 20˚C is used instead
                                of a setback for simulation purposes. If you would like to model a setback, see the
                                dedicated checkbox in the Temperatures tab. However, this will result in more unmet
                                heating hours than expected.
                            </li>
                        </ul>
                        <p>
                            These limitations, along with the ability to control non-HOT2000 inputs for EnergyPlus
                            simulations, will be addressed in future updates.
                        </p>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    energyPlusResults: selectEnergyPlusResults,
    modelName: selectModelName,
});

const mapDispatchToProps = (dispatch) => ({
    runEnergyPlus: (modelId, expVersion, upgradePackageApplied) =>
        dispatch(runEnergyPlus(modelId, expVersion, upgradePackageApplied)),
    fetchEnergyPlusResults: (modelId) => dispatch(fetchEnergyPlusResults(modelId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnergyPlus);
